import { Brain, ChevronDown, ChevronUp, Power, PowerOff } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/admin/shared/badge'
import { CalculationLogView } from './calculation-log-view'
import { GraphView } from './graph-view'
import { HomeworkReviewView } from './homework-review-view'
import { PendingChangesModal } from './pending-changes-modal'
import { ProficiencyGoalsView } from './proficiency-goals-view'
import { ProgressView } from './progress-view'
import { useRecommendationEngine } from './recommendation-engine-context'
import { RecommendationEngineNoData } from './recommendation-engine-data-placeholders'
import { RecommendationEngineStagedProvider } from './recommendation-engine-real-context'
import { RecommendationEngineTabs } from './recommendation-engine-tabs'
import { RecommendationsView } from './recommendations-view'
import { ReprocessControlBar } from './reprocess-control-bar'
import { SetupView } from './setup-view'

interface RecommendationEngineSectionProps {
  /** Client name, shown in the section subtitle. Display-only. */
  clientId?: string
  clientName?: string
  baselineEpochAt?: Date | string | null
  baselineEpochTimeZone?: string | null
  engineEnabled?: boolean
  onSetBaselineEpoch?: (date: string) => Promise<void>
  onUpdateBaselineEpoch?: (date: string) => Promise<void>
  onClearBaselineEpoch?: () => Promise<void>
}

/** Left → right: day-to-day coaching first; Setup last (onboarding / rare). */
const TABS = [
  { id: 'progress', label: 'Progress' },
  { id: 'proficiency-goals', label: 'Proficiency Goals' },
  { id: 'graph', label: 'Graph' },
  { id: 'recommendations', label: 'Recommendations' },
  { id: 'homework-review', label: 'Homework Review' },
  { id: 'calculation-log', label: 'Calculation Log' },
  { id: 'setup', label: 'Setup' },
] as const

export type RecommendationEngineTabId = (typeof TABS)[number]['id']

function RecommendationEnginePanel({
  clientId,
  activeTab,
  setActiveTab,
}: {
  clientId?: string
  activeTab: RecommendationEngineTabId
  setActiveTab: (tabId: RecommendationEngineTabId) => void
}) {
  const {
    pendingDiff,
    dirtyTabs,
    pendingProcessingTypes,
    pendingProcessingSummary,
    canRevertToSnapshot,
    triggerProcessingFromPendingChanges,
    revertToLastSnapshot,
  } = useRecommendationEngine()
  const [pendingModalOpen, setPendingModalOpen] = useState(false)

  return (
    <div className="border-border space-y-4 border-t p-4">
      <ReprocessControlBar
        pendingCount={pendingDiff.length}
        dirtyTabs={dirtyTabs}
        summaryLabel={pendingProcessingSummary}
        processingTypes={pendingProcessingTypes}
        onOpenPendingChanges={() => setPendingModalOpen(true)}
        onRunProcessing={triggerProcessingFromPendingChanges}
        canRevertToSnapshot={canRevertToSnapshot}
        onRevertToSnapshot={revertToLastSnapshot}
      />
      <PendingChangesModal
        isOpen={pendingModalOpen}
        pendingChanges={pendingDiff}
        onClose={() => setPendingModalOpen(false)}
      />
      {pendingDiff.length === 0 && (
        <RecommendationEngineNoData message="No pending recommendation-engine changes yet." />
      )}
      <RecommendationEngineTabs
        tabs={[...TABS]}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as RecommendationEngineTabId)}
      />
      <div>
        {activeTab === 'progress' && <ProgressView />}
        {activeTab === 'proficiency-goals' && <ProficiencyGoalsView />}
        {activeTab === 'graph' && <GraphView clientId={clientId} />}
        {activeTab === 'recommendations' && <RecommendationsView />}
        {activeTab === 'homework-review' && <HomeworkReviewView />}
        {activeTab === 'calculation-log' && <CalculationLogView />}
        {activeTab === 'setup' && <SetupView />}
      </div>
    </div>
  )
}

/**
 * The Recommendation Engine section on the client detail page. A collapsible
 * card holding a tabbed panel of the coach-facing views.
 *
 * NOTE: behavior remains mock-backed for now, but the staged provider also
 * probes real setup readiness via `admin.recommendationEngine.getSetupState`
 * behind a feature gate.
 */
export function RecommendationEngineSection({
  clientId,
  clientName,
  baselineEpochAt = null,
  baselineEpochTimeZone = null,
  engineEnabled = true,
  onSetBaselineEpoch,
  onUpdateBaselineEpoch,
  onClearBaselineEpoch,
}: RecommendationEngineSectionProps) {
  const [collapsed, setCollapsed] = useState(true)
  const [activeTab, setActiveTab] =
    useState<RecommendationEngineTabId>('progress')

  return (
    <div className="bg-card border-border rounded-lg border">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="hover:bg-muted/50 flex w-full items-center justify-between p-4 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 p-2">
            <Brain size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-foreground flex items-center gap-2 text-lg font-semibold">
              Recommendation Engine
              {engineEnabled ? (
                <Badge variant="active">
                  <span className="inline-flex items-center gap-1">
                    <Power size={12} />
                    Engine on
                  </span>
                </Badge>
              ) : (
                <Badge variant="paused">
                  <span className="inline-flex items-center gap-1">
                    <PowerOff size={12} />
                    Engine off
                  </span>
                </Badge>
              )}
            </h2>
            <p className="text-muted-foreground text-sm">
              {clientName
                ? `Knowledge map, recommendations & audit trail for ${clientName}`
                : 'Knowledge map, recommendations & audit trail'}
            </p>
          </div>
        </div>
        {collapsed ? (
          <ChevronDown size={20} className="text-muted-foreground" />
        ) : (
          <ChevronUp size={20} className="text-muted-foreground" />
        )}
      </button>

      {!collapsed && (
        <RecommendationEngineStagedProvider
          clientId={clientId}
          baselineEpochAt={baselineEpochAt}
          baselineEpochTimeZone={baselineEpochTimeZone}
          engineEnabled={engineEnabled}
          onSetBaselineEpoch={onSetBaselineEpoch}
          onUpdateBaselineEpoch={onUpdateBaselineEpoch}
          onClearBaselineEpoch={onClearBaselineEpoch}
        >
          <RecommendationEnginePanel
            clientId={clientId}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </RecommendationEngineStagedProvider>
      )}
    </div>
  )
}
